import { useMemo, useState } from 'react';
import {
  CircleMarker,
  MapContainer,
  Polygon,
  Popup,
  TileLayer,
  Tooltip,
} from 'react-leaflet';
import { Link } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import {
  ALL_YEAR_BANDS_FOR_CRAWL,
  YEAR_BAND_ANY,
  ZONES_BUNDLE,
  type LeadMapPin,
  type MarketPriceItem,
  type Region,
  type Tier,
  type ZoneEntry,
} from '@auctionradar/shared';
import { useLeadMapPins, useZonePrices } from '../api/hooks';
import { fmtEur } from '../components/SourceRow';

const ATTICA_CENTER: [number, number] = [37.99, 23.75];
const ATTICA_ZOOM = 10;

const pct = (n: number | null | undefined): string =>
  n == null ? '—' : `${Math.round(n * 100)}%`;

// ─── Polygon sanity (coverage signal — only real boundaries are drawn) ──────

const MAX_ZONE_BBOX_DEG = 0.15;
const MIN_VERTEX_COUNT = 8;
const SKIPPED_SOURCES: ReadonlySet<string> = new Set([
  'macro_synthesized',
  'parent_inherited',
  'voronoi',
  'nominatim_bbox',
]);

function isPolygonSane(zone: ZoneEntry): boolean {
  if (!zone.bbox || !zone.rings || zone.rings.length === 0) return false;
  if (SKIPPED_SOURCES.has(zone.polygon_source)) return false;
  const dLng = zone.bbox[2] - zone.bbox[0];
  const dLat = zone.bbox[3] - zone.bbox[1];
  if (dLng > MAX_ZONE_BBOX_DEG || dLat > MAX_ZONE_BBOX_DEG) return false;
  const totalVertices = zone.rings.reduce((sum, r) => sum + r.length, 0);
  if (totalVertices < MIN_VERTEX_COUNT) return false;
  return true;
}

// ─── L2 grouping colors (same color = same municipality) ────────────────────

const L2_PALETTE = [
  '#1f3a5c', '#b5651d', '#2d6a4f', '#9d4edd', '#c1121f', '#0077b6',
  '#bc6c25', '#3a5a40', '#6a4c93', '#9a031e', '#118ab2', '#e07a5f',
];

const L2_COLOR_BY_SLUG: ReadonlyMap<string, string> = (() => {
  const l2s = ZONES_BUNDLE.zones.filter(
    (z) => z.level === 2 && Array.isArray(z.bbox) && z.bbox.length === 4,
  );
  const EPS = 0.004;
  const neighbors = new Map<string, string[]>();
  for (const z of l2s) neighbors.set(z.slug, []);
  for (let i = 0; i < l2s.length; i++) {
    const a = l2s[i]!;
    const ab = a.bbox as [number, number, number, number];
    for (let j = i + 1; j < l2s.length; j++) {
      const b = l2s[j]!;
      const bb = b.bbox as [number, number, number, number];
      if (ab[0] - EPS > bb[2] || bb[0] - EPS > ab[2]) continue;
      if (ab[1] - EPS > bb[3] || bb[1] - EPS > ab[3]) continue;
      neighbors.get(a.slug)!.push(b.slug);
      neighbors.get(b.slug)!.push(a.slug);
    }
  }
  const order = [...l2s].sort(
    (x, y) => neighbors.get(y.slug)!.length - neighbors.get(x.slug)!.length,
  );
  const color = new Map<string, number>();
  for (const z of order) {
    const used = new Set<number>();
    for (const n of neighbors.get(z.slug)!) {
      const c = color.get(n);
      if (c != null) used.add(c);
    }
    let c = 0;
    while (used.has(c)) c++;
    color.set(z.slug, c % L2_PALETTE.length);
  }
  const out = new Map<string, string>();
  for (const [slug, idx] of color) out.set(slug, L2_PALETTE[idx]!);
  return out;
})();

function l2Color(slug: string): string {
  const c = L2_COLOR_BY_SLUG.get(slug);
  if (c) return c;
  let h = 0;
  for (let i = 0; i < slug.length; i++) h = (h * 31 + slug.charCodeAt(i)) >>> 0;
  return L2_PALETTE[h % L2_PALETTE.length]!;
}

function findL2Ancestor(zone: ZoneEntry): string | null {
  if (zone.level === 2) return zone.slug;
  if (zone.level === 1) return null;
  if (!zone.parent_slug) return null;
  const parentIdx = ZONES_BUNDLE.by_slug[zone.parent_slug];
  if (parentIdx == null) return null;
  const parent = ZONES_BUNDLE.zones[parentIdx];
  if (!parent) return null;
  return parent.level === 2 ? parent.slug : findL2Ancestor(parent);
}

// ─── Filters ────────────────────────────────────────────────────────────────

type Filters = {
  region: Region | undefined;
  parentL2: string | null;
  search: string;
};

function matchesFilters(zone: ZoneEntry, filters: Filters): boolean {
  if (filters.region && zone.region !== filters.region) return false;
  if (filters.parentL2) {
    if (zone.slug !== filters.parentL2) {
      const ancestor = findL2Ancestor(zone);
      if (ancestor !== filters.parentL2) return false;
    }
  }
  if (filters.search.trim()) {
    const q = filters.search.trim().toLowerCase();
    const slug = zone.slug.toLowerCase();
    const name = (zone.name_el || '').toLowerCase();
    if (!slug.includes(q) && !name.includes(q)) return false;
  }
  return true;
}

// ─── Pin filter ──────────────────────────────────────────────────────────────

type PinFilter = 'none' | 'A' | 'B' | 'C' | 'D' | 'rest' | 'all';
const PIN_OPTIONS: Array<{ value: PinFilter; label: string }> = [
  { value: 'none', label: 'Καμία' },
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
  { value: 'D', label: 'D' },
  { value: 'rest', label: 'Rest' },
  { value: 'all', label: 'Όλα' },
];

const TIER_COLOR: Record<string, string> = {
  A: '#059669',
  B: '#2563eb',
  C: '#d97706',
  D: '#64748b',
};
const REST_COLOR = '#9ca3af';
const pinColor = (tier: Tier | null): string =>
  tier ? (TIER_COLOR[tier] ?? REST_COLOR) : REST_COLOR;

function pinMatches(pin: LeadMapPin, f: PinFilter): boolean {
  switch (f) {
    case 'none': return false;
    case 'all': return true;
    case 'rest': return pin.tier == null;
    default: return pin.tier === f;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function PriceMapPage() {
  const [filters, setFilters] = useState<Filters>({
    region: undefined,
    parentL2: null,
    search: '',
  });
  const [pinFilter, setPinFilter] = useState<PinFilter>('none');
  const [selectedSlug, setSelectedSlug] = useState<string | null>(null);

  const pinsQuery = useLeadMapPins();
  const pins = useMemo(() => {
    const all = pinsQuery.data?.items ?? [];
    return all.filter((p) => pinMatches(p, pinFilter));
  }, [pinsQuery.data, pinFilter]);

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col">
      <FilterBar
        filters={filters}
        setFilters={setFilters}
        pinFilter={pinFilter}
        setPinFilter={setPinFilter}
        pinCount={pins.length}
      />
      <div className="relative flex-1 overflow-hidden rounded-lg border border-slate-200 bg-white">
        <MapContainer
          center={ATTICA_CENTER}
          zoom={ATTICA_ZOOM}
          minZoom={8}
          maxZoom={17}
          preferCanvas
          className="h-full w-full"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <ZoneLayer filters={filters} onSelect={setSelectedSlug} selectedSlug={selectedSlug} />
          <PinLayer pins={pins} />
        </MapContainer>
      </div>

      {selectedSlug && (
        <ZonePriceModal slug={selectedSlug} onClose={() => setSelectedSlug(null)} />
      )}
    </div>
  );
}

// ─── Filter bar ──────────────────────────────────────────────────────────────

function FilterBar({
  filters,
  setFilters,
  pinFilter,
  setPinFilter,
  pinCount,
}: {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  pinFilter: PinFilter;
  setPinFilter: (f: PinFilter) => void;
  pinCount: number;
}) {
  const l2Options = useMemo(() => {
    const out: Array<{ slug: string; name_el: string }> = [];
    for (const z of ZONES_BUNDLE.zones) {
      if (z.level !== 2) continue;
      if (filters.region && z.region !== filters.region) continue;
      out.push({ slug: z.slug, name_el: z.name_el });
    }
    out.sort((a, b) => a.name_el.localeCompare(b.name_el, 'el'));
    return out;
  }, [filters.region]);

  return (
    <div className="mb-3 space-y-2 rounded-lg border border-slate-200 bg-white p-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="mr-3 text-xl font-semibold text-slate-900">Χάρτης κάλυψης</h1>
        <div className="flex items-center gap-1">
          {(['attica', 'corinth', undefined] as const).map((r) => (
            <button
              key={r ?? 'all'}
              onClick={() => setFilters((f) => ({ ...f, region: r, parentL2: null }))}
              className={`rounded border px-2.5 py-1 text-xs ${
                filters.region === r
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              {r === 'attica' ? 'Αττική' : r === 'corinth' ? 'Κορινθία' : 'Όλα'}
            </button>
          ))}
        </div>
        <select
          value={filters.parentL2 ?? ''}
          onChange={(e) =>
            setFilters((f) => ({ ...f, parentL2: e.target.value || null }))
          }
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs"
        >
          <option value="">Όλες οι γειτονιές</option>
          {l2Options.map((o) => (
            <option key={o.slug} value={o.slug}>
              {o.name_el}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Αναζήτηση ζώνης…"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          className="ml-auto w-64 rounded border border-slate-200 px-3 py-1 text-sm placeholder:text-slate-400 focus:border-slate-400 focus:outline-none"
        />
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className="mr-1 text-slate-500">Pins ακινήτων:</span>
        {PIN_OPTIONS.map((o) => {
          const active = pinFilter === o.value;
          const dot =
            o.value === 'A' || o.value === 'B' || o.value === 'C' || o.value === 'D'
              ? TIER_COLOR[o.value]
              : o.value === 'rest'
                ? REST_COLOR
                : null;
          return (
            <button
              key={o.value}
              onClick={() => setPinFilter(o.value)}
              className={`flex items-center gap-1 rounded border px-2.5 py-1 ${
                active
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-200 bg-white text-slate-700'
              }`}
            >
              {dot && (
                <span
                  className="inline-block h-2 w-2 rounded-full"
                  style={{ backgroundColor: dot }}
                />
              )}
              {o.label}
            </button>
          );
        })}
        {pinFilter !== 'none' && (
          <span className="text-slate-400">{pinCount} pins</span>
        )}
      </div>
    </div>
  );
}

// ─── Zone (coverage) layer ───────────────────────────────────────────────────

function ZoneLayer({
  filters,
  onSelect,
  selectedSlug,
}: {
  filters: Filters;
  onSelect: (slug: string) => void;
  selectedSlug: string | null;
}) {
  const l3s = useMemo(
    () =>
      ZONES_BUNDLE.zones.filter(
        (z) =>
          z.level === 3 &&
          z.rings &&
          z.rings.length > 0 &&
          isPolygonSane(z) &&
          matchesFilters(z, filters),
      ),
    [filters],
  );

  return (
    <>
      {l3s.map((z) => {
        const fillColor = l2Color(z.parent_slug || z.slug);
        const isSelected = selectedSlug === z.slug;
        return z.rings!.map((ring, idx) => {
          const positions = ring.map(([lng, lat]) => [lat, lng]) as [number, number][];
          return (
            <Polygon
              key={`${z.slug}-${idx}`}
              positions={positions}
              pathOptions={{
                fillColor,
                fillOpacity: 0.5,
                color: isSelected ? '#0f172a' : '#64748b',
                weight: isSelected ? 2.5 : 1,
              }}
              eventHandlers={{ click: () => onSelect(z.slug) }}
            >
              <Tooltip direction="top" sticky>
                <div className="text-xs">
                  <div className="font-semibold">{z.name_el}</div>
                  <div className="text-slate-500">
                    L{z.level} · {z.region === 'corinth' ? 'Κορινθία' : 'Αττική'}
                  </div>
                  <div className="text-slate-400">Κλικ για δεδομένα τιμών</div>
                </div>
              </Tooltip>
            </Polygon>
          );
        });
      })}
    </>
  );
}

// ─── Pin layer ───────────────────────────────────────────────────────────────

function PinLayer({ pins }: { pins: ReadonlyArray<LeadMapPin> }) {
  return (
    <>
      {pins.map((p) => (
        <CircleMarker
          key={p.id}
          center={[p.geoLat, p.geoLng]}
          radius={5}
          pathOptions={{
            color: '#ffffff',
            weight: 1.5,
            fillColor: pinColor(p.tier),
            fillOpacity: 0.95,
          }}
        >
          <Popup>
            <PinPopup pin={p} />
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

function PinPopup({ pin }: { pin: LeadMapPin }) {
  const perSqm =
    pin.startingPrice != null && pin.sqm != null && pin.sqm > 0
      ? pin.startingPrice / pin.sqm
      : null;
  return (
    <div className="min-w-[180px] space-y-1 text-xs">
      <div className="flex items-center gap-2">
        <span
          className="inline-block h-2.5 w-2.5 rounded-full"
          style={{ backgroundColor: pinColor(pin.tier) }}
        />
        <span className="font-semibold text-slate-900">
          {pin.tier ? `Tier ${pin.tier}` : 'Χωρίς score'}
        </span>
        {pin.roundNumber != null && (
          <span className="text-slate-500">· Γύρος {pin.roundNumber}</span>
        )}
      </div>
      <div className="text-slate-600">{pin.municipality ?? pin.zoneSlug ?? '—'}</div>
      <div className="text-slate-600">
        {pin.sqm != null ? `${Math.round(pin.sqm)} τμ · ` : ''}
        {fmtEur(pin.startingPrice)}
        {perSqm != null ? ` · ${fmtEur(perSqm)}/m²` : ''}
      </div>
      {pin.auctionDate && <div className="text-slate-500">Δημοπρασία: {pin.auctionDate}</div>}
      <div className="flex flex-wrap gap-2 pt-1">
        <Link
          to={`/leads/${pin.id}`}
          target="_blank"
          rel="noreferrer"
          className="font-medium text-rose-600 hover:underline"
        >
          Άνοιγμα lead →
        </Link>
        {pin.eauctionUrl && (
          <a
            href={pin.eauctionUrl}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-slate-600 hover:underline"
          >
            eauction ↗
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Zone price modal ────────────────────────────────────────────────────────

const SQM_ORDER: ReadonlyArray<string> = ['30-50', '50-80', '80-110', '110-140', '140+'];
// Year columns in chronological order; '*' (any) shown last as "όλα".
const YEAR_ORDER: ReadonlyArray<string> = ['<1960', ...ALL_YEAR_BANDS_FOR_CRAWL, YEAR_BAND_ANY];
const yearLabel = (y: string): string => (y === YEAR_BAND_ANY ? 'όλα' : y);

function ZonePriceModal({ slug, onClose }: { slug: string; onClose: () => void }) {
  const { data, isLoading } = useZonePrices(slug);
  const rows = useMemo(() => data?.items ?? [], [data]);
  const zone = useMemo(() => {
    const idx = ZONES_BUNDLE.by_slug[slug];
    return idx != null ? ZONES_BUNDLE.zones[idx] ?? null : null;
  }, [slug]);

  // Pivot rows into a sqm × year matrix; keep only the bands that have data.
  const { byKey, sqmBands, yearBands } = useMemo(() => {
    const map = new Map<string, MarketPriceItem>();
    const sqmSet = new Set<string>();
    const yearSet = new Set<string>();
    for (const r of rows) {
      map.set(`${r.sqmBand}|${r.yearBand}`, r);
      sqmSet.add(r.sqmBand);
      yearSet.add(r.yearBand);
    }
    return {
      byKey: map,
      sqmBands: SQM_ORDER.filter((s) => sqmSet.has(s)),
      yearBands: YEAR_ORDER.filter((y) => yearSet.has(y)),
    };
  }, [rows]);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
      onClick={onClose}
    >
      <div
        className="max-h-[85vh] w-full max-w-5xl overflow-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-4 py-3">
          <div>
            <div className="text-base font-semibold text-slate-900">
              {zone?.name_el ?? slug}
            </div>
            <div className="font-mono text-[10px] text-slate-400">{slug}</div>
          </div>
          <button
            onClick={onClose}
            className="rounded px-2 py-1 text-sm text-slate-500 hover:bg-slate-100"
          >
            ✕
          </button>
        </div>

        <div className="p-4">
          {isLoading ? (
            <div className="h-24 animate-pulse rounded bg-slate-100" />
          ) : rows.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500">
              Δεν υπάρχουν δεδομένα τιμών για αυτή τη ζώνη.
            </div>
          ) : (
            <>
              <table className="border-collapse text-xs">
                <thead>
                  <tr>
                    <th className="sticky left-0 z-10 border border-slate-200 bg-slate-100 px-2 py-1.5 text-left font-semibold text-slate-600">
                      τμ \ έτος
                    </th>
                    {yearBands.map((y) => (
                      <th
                        key={y}
                        className="border border-slate-200 bg-slate-50 px-2 py-1.5 text-center font-semibold text-slate-600"
                      >
                        {yearLabel(y)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sqmBands.map((s) => (
                    <tr key={s}>
                      <th className="sticky left-0 z-10 border border-slate-200 bg-slate-100 px-2 py-1.5 text-left font-semibold text-slate-700 whitespace-nowrap">
                        {s} τμ
                      </th>
                      {yearBands.map((y) => (
                        <MatrixCell key={y} item={byKey.get(`${s}|${y}`) ?? null} />
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="mt-3 text-[10px] text-slate-400">
                Πάνω: υπολογισμένη τιμή (mid €/m²). Κάτω: πηγές — Spit =
                Spitogatos ×0.80 · XE = xe.gr ×0.80 · iA = iauction (R1, fallback
                R3). Το «·n» = αριθμός listings.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/** One sqm×year cell: calculated mid on top, per-source values beneath. */
function MatrixCell({ item }: { item: MarketPriceItem | null }) {
  if (!item || item.pricePerSqmMid == null) {
    return (
      <td className="border border-slate-100 px-2 py-1.5 text-center align-top text-slate-300">
        —
      </td>
    );
  }
  const ia = item.iauctionR1Appraised ?? item.iauctionR3Stressed;
  const iaIsR3 = item.iauctionR1Appraised == null && item.iauctionR3Stressed != null;
  const iaCount = item.iauctionR1Appraised != null ? item.iauctionR1Count : item.iauctionR3Count;
  return (
    <td className="border border-slate-100 px-2 py-1.5 text-center align-top">
      <div className="text-sm font-semibold text-slate-900">
        {fmtEur(item.pricePerSqmMid)}
      </div>
      <div className="text-[9px] leading-tight text-slate-400">
        {item.spitogatosAdjusted != null && (
          <div>
            Spit {fmtEur(item.spitogatosAdjusted)}
            {item.spitogatosCount > 0 && <span>·{item.spitogatosCount}</span>}
          </div>
        )}
        {item.xeAdjusted != null && (
          <div>
            XE {fmtEur(item.xeAdjusted)}
            {item.xeCount > 0 && <span>·{item.xeCount}</span>}
          </div>
        )}
        {ia != null && (
          <div>
            iA{iaIsR3 ? '·R3' : ''} {fmtEur(ia)}
            {iaCount > 0 && <span>·{iaCount}</span>}
          </div>
        )}
      </div>
      {item.confidenceScore != null && (
        <div className="mt-0.5 text-[8px] text-slate-300">conf {pct(item.confidenceScore)}</div>
      )}
    </td>
  );
}
